"use client";

import { motion } from "framer-motion";
import { FileText, CheckCircle, Target } from "lucide-react";
import SectionHero from "../components/SectionHero";
import ContentSection from "../components/ContentSection";
import { useEffect } from "react";

const RegistrySurvey = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const steps = [
    {
      number: "1",
      title: "Flag Selection Guidance",
      description:
        "Evaluate registration options based on intended trading areas, crew patterns, taxation, and regulatory frameworks.",
    },
    {
      number: "2",
      title: "Documentation Preparation",
      description:
        "Provide comprehensive checklist of required documents and support gathering of all necessary materials.",
    },
    {
      number: "3",
      title: "Administration Liaison",
      description:
        "Coordinate with flag administration and recognised organisations throughout the registration process.",
    },
    {
      number: "4",
      title: "Survey Coordination",
      description:
        "Plan survey timing to minimize operational disruption and coordinate with all stakeholders.",
    },
    {
      number: "5",
      title: "Certificate Issuance",
      description:
        "Support the complete issuance or transfer of key certificates and ensure documentation consistency.",
    },
  ];

  const factors = [
    {
      title: "Trading Areas",
      description: "Consider intended voyage routes and cargo types",
    },
    {
      title: "Crew Nationality",
      description: "Evaluate crew composition and training requirements",
    },
    {
      title: "Regulatory Framework",
      description: "Review taxation, fees, and compliance requirements",
    },
    {
      title: "Flag Reputation",
      description: "Assess track record and international standing",
    },
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Registry & Survey"
        subtitle="Clarity and Support for Registration and Flag Change"
        description="Choosing the right flag is an important strategic decision. We help you understand implications and navigate the process."
      />

      <ContentSection title="Our Comprehensive Process">
        <div className="space-y-6 md:space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="flex gap-6 rounded-xl bg-slate-50 p-6 md:p-8"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-teal-600 text-white font-bold md:h-16 md:w-16 md:text-xl">
                {step.number}
              </div>
              <div>
                <h3 className="mb-2 text-lg font-bold text-slate-900 md:text-xl">
                  {step.title}
                </h3>
                <p className="text-slate-700">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </ContentSection>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              Flag Selection Factors
            </h2>
            <p className="mx-auto max-w-3xl text-slate-600">
              We evaluate multiple factors to help you make an informed decision
              about vessel registration.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {factors.map((factor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-white p-6 text-center shadow-lg"
              >
                <Target className="mx-auto mb-4 h-8 w-8 text-teal-600" />
                <h3 className="mb-2 font-bold text-slate-900">
                  {factor.title}
                </h3>
                <p className="text-sm text-slate-600">{factor.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">
                Controlled Project Approach
              </h2>
              <p className="mb-6 text-slate-700">
                Our target is to make registration and flag change a controlled
                project instead of a stressful last-minute rush. We take
                responsibility for execution and follow-up, ensuring smoother
                processes and fewer surprises.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Clear timeline and milestones
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Transparent communication
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Proactive issue resolution
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Complete documentation package
                  </span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100 p-8"
            >
              <FileText className="mb-4 h-12 w-12 text-teal-600" />
              <h3 className="mb-4 text-2xl font-bold text-slate-900">
                What We Handle
              </h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start space-x-3">
                  <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-teal-600"></div>
                  <span>Initial registration reviews</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-teal-600"></div>
                  <span>Flag administration liaison</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-teal-600"></div>
                  <span>Survey coordination and scheduling</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-teal-600"></div>
                  <span>Condition and certificate closeouts</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegistrySurvey;
